import type { FC } from 'react';
import React, { useMemo } from 'react';
import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { LabelColor } from '@pflegenavi/web-components';
import { Label, makeConfig, PaginatedTable2 } from '@pflegenavi/web-components';
import {
  useFormatDate,
  useFormatting,
} from '@pflegenavi/frontend/localization';
import type { GetPayoutsResult } from '@pflegenavi/frontend/api-nursing-home';
import { useGetPayoutsPhoenix } from '@pflegenavi/frontend/api-nursing-home';
import { useNursingHomeContext } from '@pflegenavi/frontend/nursing-home-context';
import { YEAR_SHORTMONTH_DAY_FORMAT } from '@pflegenavi/shared/constants';
import { LinkedTableSection } from './LinkedTableSection';
import { useLinkedTablePagination } from './useLinkedTablePagination';

type Payout = GetPayoutsResult['data'][number];

type ColumnNames =
  | 'bank'
  | 'iban'
  | 'initiationDate'
  | 'amount'
  | 'transferDate'
  | 'status';

const STATUS_LABELS: Record<
  Payout['state'],
  { color: LabelColor; key: string }
> = {
  CREATED: { color: 'warning', key: 'pending' },
  SUCCEEDED: { color: 'success', key: 'completed' },
  FAILED: { color: 'error', key: 'failed' },
};

/**
 * Lists the linked nursing home payouts. Legacy Stripe payouts cannot be
 * fetched anymore, so they only show up as a note with their count.
 */
export const PayoutsTable: FC<{ ids: string[]; stripePayoutCount: number }> = ({
  ids,
  stripePayoutCount,
}) => {
  const { t } = useTranslation();
  const { fCurrencyCents } = useFormatting();
  const fDate = useFormatDate();
  const { selectedNursingHome } = useNursingHomeContext();

  const { page, pageSize, pagination } = useLinkedTablePagination(ids.length);

  const { data, isLoading } = useGetPayoutsPhoenix(
    {
      params: {
        nursing_home_id: selectedNursingHome?.id ?? '',
        page,
        pageSize,
        state: undefined,
        ids,
      },
    },
    { enabled: selectedNursingHome !== undefined && ids.length > 0 }
  );

  const config = useMemo(
    () =>
      makeConfig<Payout, ColumnNames, never>({
        showAll: undefined,
        hideEmptyRows: true,
        columns: [
          {
            key: 'bank',
            label: t('accounting.payouts.table-head.bank'),
            align: 'left',
            component: ({ row }) => (
              <Typography variant="subtitle2" noWrap>
                {row.statement_descriptor}
              </Typography>
            ),
          },
          {
            key: 'iban',
            label: t('accounting.payouts.table-head.4-digits'),
            align: 'left',
            component: ({ row }) => row.iban_last_4 ?? '--',
          },
          {
            key: 'initiationDate',
            label: t('accounting.payouts.table-head.initiation-date'),
            align: 'left',
            component: ({ row }) =>
              fDate(row.inserted_at, YEAR_SHORTMONTH_DAY_FORMAT),
          },
          {
            key: 'amount',
            label: t('accounting.payouts.table-head.amount'),
            align: 'right',
            component: ({ row }) => (
              <Typography variant="subtitle2" noWrap>
                {fCurrencyCents(row.amount)}
              </Typography>
            ),
          },
          {
            key: 'transferDate',
            label: t('accounting.payouts.table-head.transfer-date'),
            align: 'left',
            component: ({ row }) =>
              row.transferred
                ? fDate(row.transferred, YEAR_SHORTMONTH_DAY_FORMAT)
                : '--',
          },
          {
            key: 'status',
            label: t('accounting.payouts.table-head.status'),
            align: 'left',
            component: ({ row }) => (
              <Label color={STATUS_LABELS[row.state].color} variant="ghost">
                {t(`accounting.payouts.states.${STATUS_LABELS[row.state].key}`)}
              </Label>
            ),
          },
        ],
        pagination,
      }),
    [t, fDate, fCurrencyCents, pagination]
  );

  const total = ids.length + stripePayoutCount;

  return (
    <LinkedTableSection
      title={t('cashManagement.group-detail.overview.linked-payouts-title')}
      count={total}
      hint={
        stripePayoutCount > 0
          ? t('cashManagement.group-detail.overview.stripe-payouts-hint', {
              count: stripePayoutCount,
            })
          : undefined
      }
    >
      {ids.length > 0 && (
        <PaginatedTable2
          name="CashTransactionGroupDetailPayoutTable"
          config={config}
          rows={data?.data ?? []}
          isLoading={isLoading}
        />
      )}
    </LinkedTableSection>
  );
};
